import {
	SendMessage,
	SetUserMessages,
	ArchiveMessage,
	setMessageHeader,
} from '../../store/actions/message-center.actions';
import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, of, switchMap } from 'rxjs';
import { FetchMessages, FetchMessagesFailed, SetMessages } from '../../store/actions/message-center.actions';
import { State } from '../../store/reducers';
import { MessageEntity } from '../../store/entities/MessageEntity';
import { filter, take, tap, withLatestFrom } from 'rxjs/operators';
import { ApiResponse } from '../../store/entities/Api/ApiResponse';
import { MessageHeadEntity } from '../../store/entities/Api/MessageHeadEntity';
import { TxApiService } from './txapi.service';
import { getSession } from '../../store/selectors/user.selectors';
import { HeartbeatService } from './heartbeat.service';

@Injectable({
	providedIn: 'root',
})
export class MessageCenterService {
	constructor(
		private store: Store<State>,
		private actions$: Actions,
		private readonly api: TxApiService,
		private readonly heartbeatService: HeartbeatService
	) {
        heartbeatService.addToHeartbeat(
            'MessageCenterService',
			() =>
				of(null)
					.pipe(
						withLatestFrom(this.store.select(getSession)),
						filter(([_, session]) => session?.staff_office > 0),
						switchMap(() => this.fetchMessageHead()),
						take(1)
					)
					.subscribe(),
			20
		);

		// addToHeartbeat runs its callback once immediately, but that happens while this service is
		// constructed from the AppModule constructor — the session is not in the store yet, so the
		// staff_office check above drops that first run and the unread badge stays empty until the
		// next tick 20s later. Measured: session restored after 0.2s, first getMessageHead at 20.4s.
		// Fetch once as soon as a staff office session actually exists; take(1) keeps it to a single
		// subscription that completes right after.
		this.store
			.select(getSession)
			.pipe(
				filter((session) => session?.staff_office > 0),
				take(1),
				switchMap(() => this.fetchMessageHead())
			)
			.subscribe();
		/*timer(0, 5000)
			.pipe(
				withLatestFrom(this.store.select(getSession)),
				filter(([_, session]) => session?.staff_office > 0),
				switchMap(() => this.callMessageHead()),
				tap((res: ApiResponse<MessageHeadEntity[]>) => {
					this.store.dispatch(setMessageHeader(res.MessageHead.map((m) => MessageHeadEntity.tryCast(m))));
				})
			)
			.subscribe();*/
	}

	public fetchMessageHead() {
		return this.callMessageHead().pipe(
			tap((res: ApiResponse<MessageHeadEntity[]>) => {
				this.store.dispatch(setMessageHeader(res.MessageHead.map((m) => MessageHeadEntity.tryCast(m))));
			})
		);
	}

	public callMessageHead() {
		return this.api.callAPI('getMessageHead').pipe(take(1));
	}

	public fetchMessages(employee_id: number, include_archived: boolean) {
		if (isNaN(employee_id)) {
			return of(null);
		}
		this.store.dispatch(FetchMessages(employee_id, include_archived));
		// FetchMessagesFailed must be awaited too, otherwise a failed request never completes
		// this observable and every route resolver built on it hangs in ResolveStart.
		return this.actions$.pipe(ofType(SetMessages, SetUserMessages, FetchMessagesFailed));
	}

	public sendMessage(to: number, message: string, employee_id: number = 0, parent: MessageEntity = null) {
		this.store.dispatch(SendMessage(message, to, employee_id, parent));
	}

	public archiveMessage(id: string, employee_id: number, archive: boolean, remove: boolean = false) {
		this.store.dispatch(ArchiveMessage(id, employee_id, archive, remove));
	}
}
